import { CommonComponentContext } from "../common/utils/CommonComponentContext";
import { useClientConfigImpl } from "../reducers/clientConfigSlice";
import useAppState from "../common/hooks/appStateHook";
import { useEffect, useMemo, useState } from "react";
import { ensureArray, intersect } from "../common/utils/arrayUtilities";

const glossary = {};

export const CommonComponentContextProvider = ({ children }) => {
    const { token, tokenData } = useAppState();
    const clientConfig = useClientConfigImpl(token);
    const [userData, setUserData] = useState({});

    useEffect(() => {
        if (tokenData.username) {
            fetch('/api/v1/user', {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: token
                }
            }).then(res => {
                if (res.ok) {
                    res.json().then(data => {
                        setUserData(data)
                    });
                }
            })
        } else {
            setUserData({});
        }
    }, [tokenData, token]);

    const credentials = useMemo(() => ({
        hasRole: (roles, { office, client, location } = { office: null, client: null, location: null }) => {
            const userRoles = tokenData.roles;
            const memberid = tokenData.memberid;
            const member_office = tokenData.member_office;
            if (!Array.isArray(roles)) {
                roles = [roles];
            }
            return roles.some(role => {
                if (role === '*') {
                    if (memberid && (office == null || member_office == null || intersect(member_office, ensureArray(office)).length > 0)) {
                        return true;
                    }
                    if (office == null) {
                        return true;
                    }
                    return userRoles && Object.keys(userRoles).some(role => {
                        let perms = userRoles[role];
                        return perms.some(p => {
                            if (p.office !== true && p.office != null && intersect(p.office, ensureArray(office)).length === 0) {
                                return false;
                            }
                            if (client && p.client !== true && p.client != null &&  intersect(p.client, ensureArray(client)).length === 0 ) {
                                return false;
                            }
                            if (!client && p.client !== true && p.client != null) {
                                return false;
                            }
                            if (location && p.location !== true && p.location != null &&  intersect(p.location, ensureArray(location)).length === 0 ) {
                                return false;
                            }
                            if (!location && p.location !== true && p.location != null) {
                                return false;
                            }
                            return true;
                        });
                    });
                } else if (role === 'member' && memberid) {
                    if (office == null || (member_office == null || intersect(member_office, ensureArray(office)).length > 0)) {
                        return true;
                    }
                    return false;
                } else if (userRoles && userRoles[role] !== undefined) {
                    if (office == null) {
                        return true;
                    }
                    let perms = userRoles[role];
                    return perms === true || perms.some(p => {                        
                        if (p.office !== true && p.office != null && office != null && office !== true && intersect(p.office, ensureArray(office)).length === 0) {
                            return false;
                        }
                        if (client && client !== true && p.client !== true && p.client != null && (Array.isArray(client) ? intersect(p.client, client).length === 0 : p.client.every(c => c != client))) {
                            return false;
                        }
                        if (!client && p.client !== true && p.client != null) {
                            return false;
                        }
                        if (location && location !== true && p.location !== true && p.location != null && (Array.isArray(location) ? intersect(p.location, location).length === 0 : p.location.every(l => l != location))) {
                            return false;
                        }
                        if (!location && p.location !== true && p.location != null) {
                            return false;
                        }
                        return true;
                    });
                }
                return false;
            })
        },
        username: tokenData.username,
        memberID: tokenData.memberid,
        memberOffice: tokenData.member_office,
        memberTZ: tokenData.member_tz,
        rostering_overrides: userData.rostering_overrides ?? {}
    }), [tokenData, userData]);

    const value = useMemo(() => ({ clientConfig, upsertSetting: () => {}, configStatuses: () => {}, glossary, credentials }), [clientConfig, credentials ]);
    
    return <CommonComponentContext.Provider value={value}>
        {children}
    </CommonComponentContext.Provider>
}